// ✅ src/pages/PublicTests.jsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import API from "../api";

export default function PublicTests() {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [sortOption, setSortOption] = useState("latest");

  // ✅ Fetch public tests from backend
  useEffect(() => {
    const fetchPublicTests = async () => {
      try {
        const res = await API.get("/publictests/public"); // backend route
        setTests(res.data || []);
      } catch (err) {
        console.error("❌ Error fetching public tests:", err);
        setError("Failed to load public tests.");
      } finally {
        setLoading(false);
      }
    };
    fetchPublicTests();
  }, []);

  // ✅ Filter + sort logic
  const filteredTests = tests
    .filter((t) => {
      const term = search.toLowerCase();
      return (
        t.title.toLowerCase().includes(term) ||
        t.description?.toLowerCase().includes(term) ||
        t.createdBy?.name?.toLowerCase().includes(term)
      );
    })
    .sort((a, b) => {
      if (sortOption === "latest") return new Date(b.createdAt) - new Date(a.createdAt);
      if (sortOption === "oldest") return new Date(a.createdAt) - new Date(b.createdAt);
      return a.title.localeCompare(b.title);
    });

  if (loading) return <p className="text-center mt-10">⏳ Loading public tests...</p>;
  if (error) return <p className="text-center text-red-500 mt-10">{error}</p>;

  return (
    <div className="max-w-6xl mx-auto p-8">
      <h1 className="text-3xl font-bold text-center mb-6 text-blue-700">
        🌍 Explore Public Tests
      </h1>

      {/* 🔍 Search + Sort Controls */}
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-6">
        <input
          type="text"
          placeholder="Search tests..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full sm:w-2/3 border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />

        <select
          value={sortOption}
          onChange={(e) => setSortOption(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="latest">Newest First</option>
          <option value="oldest">Oldest First</option>
          <option value="alphabetical">Alphabetical (A–Z)</option>
        </select>
      </div>

      {/* 🧾 Test List */}
      {filteredTests.length === 0 ? (
        <p className="text-center text-gray-600">No public tests found.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTests.map((test) => (
            <div
              key={test._id}
              className="p-6 bg-white shadow-md rounded-lg hover:shadow-lg transition-shadow"
            >
              <h2 className="text-xl font-semibold mb-2">{test.title}</h2>
              <p className="text-gray-600 mb-2 line-clamp-2">
                {test.description || "No description provided"}
              </p>
              <p className="text-sm text-gray-500 mb-1">
                Questions: {test.questions?.length || 0}
              </p>
              {test.createdBy && (
                <p className="text-xs text-gray-400 mb-3">
                  👤 By: {test.createdBy.name || "Unknown"}
                </p>
              )}
              <Link
                to={`/test/${test.type || "practice"}/${test._id}`}

                className="block text-center bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                Attempt Test
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
